// Shared store for completed tasks from Inbound and Outbound processes

export interface CompletedTask {
  id: string;
  warehouseId?: number | null;
  gateEntryNumber: string;
  clientName: string;
  invoiceNumber: string;
  type: 'Inbound' | 'Outbound';
  vehicleNumber: string;
  driverName: string;
  completedDate: string;
  completedTime: string;
  products: {
    productDescription: string;
    itemCode: string;
    quantity: string;
    unitType?: string;
    warehouseLocation: string;
    batchNo: string;
    goodQty?: string;
    cDam?: string;
    dam?: string;
  }[];
  dockNumber?: string;
}

class CompletedTasksStore {
  private tasks: CompletedTask[] = [];
  private listeners: Array<() => void> = [];

  constructor() {
    // Load from localStorage on initialization
    this.loadFromStorage();
  }

  private loadFromStorage() {
    try {
      const stored = localStorage.getItem('completedTasks');
      if (stored) {
        this.tasks = JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading completed tasks:', error);
    }
  }

  private saveToStorage() {
    try {
      localStorage.setItem('completedTasks', JSON.stringify(this.tasks));
    } catch (error) {
      console.error('Error saving completed tasks:', error);
    }
  }

  addTask(task: CompletedTask) {
    this.tasks.push(task);
    this.saveToStorage();
    this.notifyListeners();
  }

  getTasks(): CompletedTask[] {
    return [...this.tasks];
  }

  getTasksForWarehouse(warehouseId?: number | null): CompletedTask[] {
    if (!warehouseId) {
      return [...this.tasks];
    }

    return this.tasks.filter((task) => task.warehouseId === warehouseId);
  }

  getTasksByType(type: 'Inbound' | 'Outbound'): CompletedTask[] {
    return this.tasks.filter(task => task.type === type);
  }

  getTasksByDateRange(startDate: string, endDate: string): CompletedTask[] {
    return this.tasks.filter(task => {
      return task.completedDate >= startDate && task.completedDate <= endDate;
    });
  }

  subscribe(listener: () => void) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener());
  }

  clearAll() {
    this.tasks = [];
    this.saveToStorage();
    this.notifyListeners();
  }
}

export const completedTasksStore = new CompletedTasksStore();
